import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  User,
  Phone,
  MapPin,
  LogOut,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    const customerData = localStorage.getItem('nas_customer');
    if (!customerData) {
      navigate('/nas-portal/login');
      return;
    }
    const parsed = JSON.parse(customerData);
    setCustomer(parsed);
    setFormData({
      name: parsed.name || '',
      phone: parsed.phone || '',
      address: parsed.address || ''
    });
  }, [navigate]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      localStorage.removeItem('nas_customer_token');
      localStorage.removeItem('nas_customer');
      localStorage.removeItem('nas_customer_cart');
      toast.success('Logged out successfully');
      navigate('/nas-portal/login');
    }
  };

  if (!customer) return null;

  return (
    <div className="p-4 pb-20 lg:pb-4 space-y-4 max-w-lg mx-auto">
      {/* Header */}
      <div className="text-center py-6">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <User size={40} className="text-green-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-800">{customer.name}</h1>
        <p className="text-sm text-slate-500">Customer Account</p>
      </div>

      {/* Profile Info */}
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="text-lg">Profile Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <User size={20} className="text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Name</p>
              <p className="font-medium">{customer.name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Phone size={20} className="text-slate-400" />
            <div>
              <p className="text-xs text-slate-500">Phone</p>
              <p className="font-medium">{customer.phone || '-'}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
            <MapPin size={20} className="text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs text-slate-500">Address</p>
              <p className="font-medium">{customer.address || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="bg-white">
        <CardContent className="p-4 space-y-3">
          <p className="text-sm text-slate-500">
            To update your profile information, please contact your supplier.
          </p>
          
          <Button 
            variant="outline" 
            className="w-full text-red-600 hover:bg-red-50"
            onClick={handleLogout}
            data-testid="profile-logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerProfile;
